import React, { Component } from "react";

import Alert from "@cloudscape-design/components/alert";
import Badge from "@cloudscape-design/components/badge";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import Container from "@cloudscape-design/components/container";
import FormField from "@cloudscape-design/components/form-field";
import Grid from "@cloudscape-design/components/grid";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import RadioGroup from "@cloudscape-design/components/radio-group";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import Tabs from "@cloudscape-design/components/tabs";
import Textarea from "@cloudscape-design/components/textarea";
import Toggle from "@cloudscape-design/components/toggle";

import WelcomeFreespinsStatisticsTab from "./WelcomeFreespinsStatisticsTab";

const MAX_BANNER_BYTES = 1024 * 1024;
const MAX_EXPIRATION_DAYS = 365;
const MAX_SIGNED_INT = 2147483647;
const MAX_UNSIGNED_INT = 4294967295;
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

const depositTypeOptions = [
	{ label: "Fiat", value: "fiat" },
	{ label: "Crypto", value: "crypto" },
	{ label: "Any", value: "any" },
];

const localizationTypeOptions = [
	{ label: "Local", value: "local" },
	{ label: "International", value: "international" },
	{ label: "Mixed", value: "mixed" },
];

const ipPeriodOptions = [
	{ label: "All time", value: "all" },
	{ label: "Custom days", value: "custom" },
];

const emptyCreateForm = () => ({
	gameId: "",
	spinsCount: "",
	betAmount: "",
	minDepositAmount: "",
	cap: "",
	expirationDays: "",
	depositType: null,
	ipMode: "all",
	ipCheckPeriodDays: "",
	title: "",
	titleType: null,
	description: "",
	descriptionType: null,
	banner: "",
	bannerPreview: "",
});

const optionByValue = (options, value) => options.find((option) => option.value === value) || null;

const parsePositiveInteger = (value, max) => {
	const rawValue = String(value).trim();
	if (!/^\d+$/.test(rawValue)) return null;

	const parsedValue = parseInt(rawValue, 10);
	if (!Number.isSafeInteger(parsedValue) || parsedValue <= 0 || parsedValue > max) return null;

	return parsedValue;
};

const validateMoney = (value, scale, maxScaledValue) => {
	if (typeof value !== "string" || !new RegExp(`^\\d+(?:\\.\\d{1,${scale}})?$`).test(value)) return false;

	const [rawInteger, rawFraction = ""] = value.split(".");
	const integer = rawInteger.replace(/^0+(?=\d)/, "");
	const scaledValue = `${integer}${rawFraction.padEnd(scale, "0")}`.replace(/^0+/, "") || "0";

	if (scaledValue === "0") return false;
	if (scaledValue.length !== maxScaledValue.length) return scaledValue.length < maxScaledValue.length;
	return scaledValue <= maxScaledValue;
};

const isPngDataUrl = (value) => typeof value === "string" && /^data:image\/png;base64,[A-Za-z0-9+/]+={0,2}$/.test(value);

const getErrorCode = (error) => {
	if (typeof error === "string") return error;
	if (typeof error?.text === "string") return error.text;
	if (typeof error?.message === "string") return error.message;
	return "";
};

const getPublicErrorMessage = (error) => {
	switch (getErrorCode(error)) {
		case "invalid_partner":
			return "This partner UID does not exist.";
		case "setting_not_found":
			return "The Welcome Freespins setting no longer exists. Current data was reloaded.";
		case "setting_active":
			return "An active Welcome Freespins setting already exists. Current data was reloaded.";
		case "setting_disabled":
			return "This Welcome Freespins setting is disabled. Current data was reloaded.";
		case "version_conflict":
			return "The setting changed in another session. Current data was reloaded; review it before trying again.";
		case "invalid_game":
			return "The selected game is no longer eligible. Choose another active listed Endorphina game.";
		case "invalid_payload":
			return "The submitted values are invalid. Review the highlighted fields.";
		case "upload_failed":
			return "The banner could not be uploaded. Select the PNG again and retry.";
		case "offer_cancellation_failed":
			return "Pending offers could not be cancelled. You can retry Disable.";
		default:
			return "The Welcome Freespins request failed. Please try again.";
	}
};

const presentationFromSetting = (setting) => ({
	title: setting?.title || "",
	titleType: optionByValue(localizationTypeOptions, setting?.titleType),
	description: setting?.description || "",
	descriptionType: optionByValue(localizationTypeOptions, setting?.descriptionType),
	banner: "",
	bannerPreview: "",
});

const ipPolicyFormFromSetting = (setting) => {
	const value = setting?.ipCheckPeriodDays;

	return {
		ipMode: value === null || value === 0 || typeof value === "undefined" ? "all" : "custom",
		ipCheckPeriodDays: value === 0 ? 0 : value === null || typeof value === "undefined" ? "" : String(value),
	};
};

const createFormFromSetting = (setting) => {
	if (!setting) return emptyCreateForm();

	return {
		gameId: String(setting.gameId || ""),
		spinsCount: String(setting.spinsCount || ""),
		betAmount: typeof setting.betAmount === "string" ? setting.betAmount : "",
		minDepositAmount: typeof setting.minDepositAmount === "string" ? setting.minDepositAmount : "",
		cap: String(setting.cap || ""),
		expirationDays: String(setting.expirationDays || ""),
		depositType: optionByValue(depositTypeOptions, setting.depositType),
		...ipPolicyFormFromSetting(setting),
		title: setting.title || "",
		titleType: optionByValue(localizationTypeOptions, setting.titleType),
		description: setting.description || "",
		descriptionType: optionByValue(localizationTypeOptions, setting.descriptionType),
		banner: "",
		bannerPreview: "",
	};
};

class WelcomeFreespinsRuleEditor extends Component {
	constructor(props) {
		super(props);

		this.currentRequestId = 0;
		this.gamesRequestId = 0;
		this.editorGeneration = 0;
		this.activeBannerReader = null;
		this.createBannerInput = null;
		this.presentationBannerInput = null;
		this.unmounted = false;
		this.statisticsCache = {};
		this.state = {
			partnerUid: "",
			loading: false,
			currentInitialized: false,
			initializationError: "",
			gamesLoading: false,
			exists: false,
			setting: null,
			games: [],
			gamesError: false,
			action: "create",
			mutating: false,
			actionError: "",
			fieldErrors: {},
			createForm: emptyCreateForm(),
			presentationForm: presentationFromSetting(null),
			ipMode: "all",
			ipCheckPeriodDays: "",
			disableConfirmationVisible: false,
			activeTabId: "configuration",
			statisticsOpened: false,
		};
	}

	componentDidMount() {
		if (this.props.visible) this.initializeEditor();
	}

	componentDidUpdate(prevProps) {
		if (this.props.visible && (!prevProps.visible || prevProps.partnerUid !== this.props.partnerUid)) {
			this.initializeEditor();
		} else if (prevProps.visible && !this.props.visible) {
			this.invalidateEditorSession();
		}
	}

	componentWillUnmount() {
		this.unmounted = true;
		this.invalidateEditorSession();
	}

	clearBannerInputs = () => {
		if (this.createBannerInput) this.createBannerInput.value = "";
		if (this.presentationBannerInput) this.presentationBannerInput.value = "";
	};

	cancelBannerReader = () => {
		const activeReader = this.activeBannerReader;
		this.activeBannerReader = null;
		this.clearBannerInputs();

		if (activeReader?.reader?.readyState === 1) activeReader.reader.abort();
	};

	advanceEditorGeneration = () => {
		this.editorGeneration += 1;
		this.cancelBannerReader();
		return this.editorGeneration;
	};

	invalidateEditorSession = () => {
		this.advanceEditorGeneration();
		this.currentRequestId += 1;
		this.gamesRequestId += 1;
	};

	isCurrentBannerReader = (generation, reader) =>
		!this.unmounted && this.props.visible && generation === this.editorGeneration && this.activeBannerReader?.reader === reader;

	initializeEditor = () => {
		const partnerUid = this.props.partnerUid === null || typeof this.props.partnerUid === "undefined" ? "" : String(this.props.partnerUid);
		const generation = this.advanceEditorGeneration();
		this.statisticsCache = {};
		this.currentRequestId += 1;
		this.gamesRequestId += 1;
		this.setState(
			{
				partnerUid,
				loading: Boolean(partnerUid),
				currentInitialized: !partnerUid,
				initializationError: "",
				gamesLoading: true,
				exists: false,
				setting: null,
				games: [],
				gamesError: false,
				action: "create",
				mutating: false,
				actionError: "",
				fieldErrors: {},
				createForm: emptyCreateForm(),
				presentationForm: presentationFromSetting(null),
				ipMode: "all",
				ipCheckPeriodDays: "",
				disableConfirmationVisible: false,
				activeTabId: "configuration",
				statisticsOpened: false,
			},
			() => {
				if (this.unmounted || generation !== this.editorGeneration || !this.props.visible) return;
				this.loadGames();
				if (partnerUid) this.loadCurrent();
			},
		);
	};

	getPartnerUid = () => parsePositiveInteger(this.state.partnerUid, MAX_SIGNED_INT);

	loadCurrent = async ({ preserveActionError = false } = {}) => {
		this.advanceEditorGeneration();
		const partnerUid = this.getPartnerUid();
		const requestId = ++this.currentRequestId;

		if (!partnerUid) {
			this.setState({ loading: false, currentInitialized: false, initializationError: "Enter one valid partner UID." });
			return false;
		}

		this.setState((state) => ({
			loading: true,
			currentInitialized: false,
			initializationError: "",
			actionError: preserveActionError ? state.actionError : "",
		}));

		try {
			const response = await this.props.Interface.welcomeFreespinsSettingsGetCurrent({ partnerUid });
			if (this.unmounted || requestId !== this.currentRequestId || !this.props.visible) return false;

			const exists = response?.exists === true && Boolean(response.setting);
			if (!exists && response?.exists !== false) throw new Error("invalid_current_response");
			const setting = exists ? response.setting : null;
			this.setState((state) => ({
				loading: false,
				currentInitialized: true,
				initializationError: "",
				exists,
				setting,
				action: exists ? null : "create",
				actionError: preserveActionError ? state.actionError : "",
				fieldErrors: {},
				createForm: createFormFromSetting(setting),
				presentationForm: presentationFromSetting(setting),
				...ipPolicyFormFromSetting(setting),
				disableConfirmationVisible: false,
			}));
			return true;
		} catch (error) {
			if (this.unmounted || requestId !== this.currentRequestId || !this.props.visible) return false;
			this.setState((state) => ({
				loading: false,
				currentInitialized: false,
				initializationError: getPublicErrorMessage(error),
				actionError: preserveActionError ? state.actionError : "",
			}));
			return false;
		}
	};

	retryCurrentInitialization = () => {
		this.loadCurrent({ preserveActionError: true });
	};

	dismissEditor = () => {
		if (this.state.mutating) return;
		this.invalidateEditorSession();
		this.props.onDismiss();
	};

	openPresentationEditor = () => {
		this.advanceEditorGeneration();
		this.setState({ action: "presentation", fieldErrors: {}, actionError: "", presentationForm: presentationFromSetting(this.state.setting) });
	};

	openIpEditor = () => {
		this.advanceEditorGeneration();
		this.setState({ action: "ip", fieldErrors: {}, actionError: "" });
	};

	closePresentationEditor = () => {
		this.advanceEditorGeneration();
		this.setState({ action: null, fieldErrors: {}, actionError: "", presentationForm: presentationFromSetting(this.state.setting) });
	};

	openCreateVersion = () => {
		this.advanceEditorGeneration();
		this.setState({ action: "create", fieldErrors: {}, actionError: "", createForm: createFormFromSetting(this.state.setting) });
	};

	loadGames = async () => {
		const requestId = ++this.gamesRequestId;
		this.setState({ gamesLoading: true, gamesError: false });

		try {
			const response = await this.props.Interface.welcomeFreespinsSettingsListEligibleGames();
			if (this.unmounted || requestId !== this.gamesRequestId || !this.props.visible) return;

			if (!Array.isArray(response?.items)) throw new Error("invalid_games_response");
			const games = response.items.map((game) => {
				if (
					!game ||
					!Number.isInteger(game.gameId) ||
					game.gameId <= 0 ||
					game.gameId > MAX_SIGNED_INT ||
					(game.gameName !== null && typeof game.gameName !== "string")
				) {
					throw new Error("invalid_games_response");
				}

				return {
					label: game.gameName || `Game #${game.gameId}`,
					value: String(game.gameId),
					description: `ID: ${game.gameId}`,
				};
			});

			this.setState({ gamesLoading: false, games, gamesError: false });
		} catch (error) {
			if (this.unmounted || requestId !== this.gamesRequestId || !this.props.visible) return;
			this.setState({ gamesLoading: false, games: [], gamesError: true });
		}
	};

	setCreateField = (field, value) => {
		this.setState((state) => ({
			createForm: { ...state.createForm, [field]: value },
			fieldErrors: { ...state.fieldErrors, [field]: "" },
			actionError: "",
		}));
	};

	setCreateIpCheck = (checked) => {
		this.setState((state) => ({
			createForm: {
				...state.createForm,
				ipMode: "all",
				ipCheckPeriodDays: checked ? "" : 0,
			},
			fieldErrors: { ...state.fieldErrors, ipCheckPeriodDays: "" },
			actionError: "",
		}));
	};

	setIpCheck = (checked) => {
		this.setState({
			ipMode: "all",
			ipCheckPeriodDays: checked ? "" : 0,
			fieldErrors: {},
			actionError: "",
		});
	};

	setPresentationField = (field, value) => {
		this.setState((state) => ({
			presentationForm: { ...state.presentationForm, [field]: value },
			fieldErrors: { ...state.fieldErrors, [field]: "" },
			actionError: "",
		}));
	};

	readBanner = (event, target) => {
		const file = event.target.files?.[0];
		this.cancelBannerReader();
		event.target.value = "";
		if (!file) return;

		const errorField = target === "create" ? "banner" : "presentationBanner";
		if (file.type !== "image/png") {
			this.setState((state) => ({ fieldErrors: { ...state.fieldErrors, [errorField]: "Select a PNG file." } }));
			return;
		}

		const generation = this.editorGeneration;
		const binaryReader = new FileReader();
		const readerSession = { reader: binaryReader };
		this.activeBannerReader = readerSession;
		binaryReader.onerror = () => {
			if (!this.isCurrentBannerReader(generation, binaryReader)) return;
			this.activeBannerReader = null;
			this.setState((state) => ({ fieldErrors: { ...state.fieldErrors, [errorField]: "The PNG could not be read." } }));
		};
		binaryReader.onload = ({ target: readerTarget }) => {
			if (!this.isCurrentBannerReader(generation, binaryReader)) return;
			const buffer = readerTarget.result;
			const bytes = new Uint8Array(buffer);
			const hasPngSignature = PNG_SIGNATURE.every((byte, index) => bytes[index] === byte);

			if (!buffer.byteLength || buffer.byteLength > MAX_BANNER_BYTES) {
				this.activeBannerReader = null;
				this.setState((state) => ({ fieldErrors: { ...state.fieldErrors, [errorField]: "Decoded PNG size must not exceed 1 MiB." } }));
				return;
			}
			if (!hasPngSignature) {
				this.activeBannerReader = null;
				this.setState((state) => ({ fieldErrors: { ...state.fieldErrors, [errorField]: "The selected file is not a valid PNG." } }));
				return;
			}

			const dataReader = new FileReader();
			readerSession.reader = dataReader;
			dataReader.onerror = () => {
				if (!this.isCurrentBannerReader(generation, dataReader)) return;
				this.activeBannerReader = null;
				this.setState((state) => ({ fieldErrors: { ...state.fieldErrors, [errorField]: "The PNG could not be read." } }));
			};
			dataReader.onload = ({ target: dataTarget }) => {
				if (!this.isCurrentBannerReader(generation, dataReader)) return;
				this.activeBannerReader = null;

				if (!isPngDataUrl(dataTarget.result)) {
					this.setState((state) => ({ fieldErrors: { ...state.fieldErrors, [errorField]: "The PNG could not be encoded." } }));
					return;
				}

				if (target === "create") {
					this.setState((state) => ({
						createForm: { ...state.createForm, banner: dataTarget.result, bannerPreview: dataTarget.result },
						fieldErrors: { ...state.fieldErrors, banner: "" },
					}));
				} else {
					this.setState((state) => ({
						presentationForm: { ...state.presentationForm, banner: dataTarget.result, bannerPreview: dataTarget.result },
						fieldErrors: { ...state.fieldErrors, presentationBanner: "" },
					}));
				}
			};
			dataReader.readAsDataURL(file);
		};
		binaryReader.readAsArrayBuffer(file);
	};

	validatePresentation = (form, bannerRequired = false) => {
		const errors = {};
		const title = form.title.trim();
		const description = form.description.trim();

		if (!title || Array.from(title).length > 255) errors.title = "Enter 1–255 characters.";
		if (!form.titleType) errors.titleType = "Select a localization type.";
		if (!description || new Blob([description]).size > 65535) errors.description = "Enter a description no larger than 65,535 UTF-8 bytes.";
		if (!form.descriptionType) errors.descriptionType = "Select a localization type.";
		if (bannerRequired && !isPngDataUrl(form.banner)) errors.banner = "Select a PNG banner.";

		return { errors, title, description };
	};

	buildCreatePayload = () => {
		const { createForm, games } = this.state;
		const presentation = this.validatePresentation(createForm, true);
		const errors = { ...presentation.errors };
		const partnerUid = this.getPartnerUid();
		const gameId = parsePositiveInteger(createForm.gameId, MAX_SIGNED_INT);
		const spinsCount = parsePositiveInteger(createForm.spinsCount, 1000);
		const cap = parsePositiveInteger(createForm.cap, MAX_UNSIGNED_INT);
		const expirationDays = parsePositiveInteger(createForm.expirationDays, MAX_EXPIRATION_DAYS);
		const ipCheckPeriodDays =
			createForm.ipCheckPeriodDays === 0
				? 0
				: createForm.ipMode === "all"
					? null
					: parsePositiveInteger(createForm.ipCheckPeriodDays, MAX_UNSIGNED_INT);

		if (!partnerUid) errors.partnerUid = "Enter one positive partner UID.";
		if (!gameId || !games.some((game) => game.value === String(gameId))) errors.gameId = "Select one active listed Endorphina game.";
		if (!spinsCount) errors.spinsCount = "Enter an integer from 1 to 1,000.";
		if (!validateMoney(createForm.betAmount, 3, "1000000")) errors.betAmount = "Enter a positive RUB amount up to 1000.000 with at most 3 decimals.";
		if (!validateMoney(createForm.minDepositAmount, 2, "99999999")) errors.minDepositAmount = "Enter a positive RUB amount up to 999999.99 with at most 2 decimals.";
		if (!cap) errors.cap = "Enter a positive integer.";
		if (!expirationDays) errors.expirationDays = "Enter an integer from 1 to 365.";
		if (!createForm.depositType) errors.depositType = "Select a deposit type.";
		if (createForm.ipCheckPeriodDays !== 0 && createForm.ipMode === "custom" && !ipCheckPeriodDays) errors.ipCheckPeriodDays = "Enter a positive integer.";

		if (Object.values(errors).some(Boolean)) return { errors, payload: null };

		const payload = {
			partnerUid,
			gameId,
			spinsCount,
			betAmount: createForm.betAmount,
			minDepositAmount: createForm.minDepositAmount,
			cap,
			expirationDays,
			depositType: createForm.depositType.value,
			ipCheckPeriodDays,
			title: presentation.title,
			titleType: createForm.titleType.value,
			description: presentation.description,
			descriptionType: createForm.descriptionType.value,
			banner: createForm.banner,
		};

		if (this.state.exists && this.state.setting && this.state.setting.enabled === false) {
			payload.expectedVersion = this.state.setting.version;
			payload.expectedRevision = this.state.setting.revision;
		}

		return { errors, payload };
	};

	finishMutation = async (message) => {
		if (this.unmounted) return;
		this.setState({ mutating: false, disableConfirmationVisible: false });
		await this.loadCurrent();
		if (this.props.onMutation) await this.props.onMutation();
		this.props.Interface.addNoty("Success", message);
	};

	handleMutationFailure = async (error) => {
		const code = getErrorCode(error);
		const shouldReload = ["version_conflict", "setting_active", "setting_disabled", "setting_not_found"].includes(code);
		this.setState({ mutating: false, actionError: getPublicErrorMessage(error), disableConfirmationVisible: false });
		if (shouldReload) await this.loadCurrent({ preserveActionError: true });
	};

	createSetting = async () => {
		const { errors, payload } = this.buildCreatePayload();
		if (!payload) {
			this.setState({ fieldErrors: errors, actionError: "Review the highlighted fields." });
			return;
		}

		this.setState({ mutating: true, fieldErrors: {}, actionError: "" });
		try {
			if (!this.state.exists) {
				const current = await this.props.Interface.welcomeFreespinsSettingsGetCurrent({ partnerUid: payload.partnerUid });
				if (current?.exists === true && current.setting) {
					const setting = current.setting;
					this.setState({
						mutating: false,
						exists: true,
						setting,
						action: null,
						actionError: setting.enabled
							? "An active setting already exists. Review the current setting."
							: "A disabled setting already exists. Review it before creating a new version.",
						fieldErrors: {},
						createForm: createFormFromSetting(setting),
						presentationForm: presentationFromSetting(setting),
						...ipPolicyFormFromSetting(setting),
					});
					return;
				}
				if (current?.exists !== false) throw new Error("invalid_current_response");
			}

			await this.props.Interface.welcomeFreespinsSettingsCreate(payload);
			await this.finishMutation("Welcome Freespins setting was created");
		} catch (error) {
			if (!this.unmounted) await this.handleMutationFailure(error);
		}
	};

	updatePresentation = async () => {
		const { setting, presentationForm } = this.state;
		const validation = this.validatePresentation(presentationForm);
		const errors = { ...validation.errors };
		if (presentationForm.banner && !isPngDataUrl(presentationForm.banner)) errors.presentationBanner = "Select a valid PNG replacement.";

		if (Object.values(errors).some(Boolean)) {
			this.setState({ fieldErrors: errors, actionError: "Review the highlighted fields." });
			return;
		}

		const payload = {
			partnerUid: this.getPartnerUid(),
			expectedVersion: setting.version,
			expectedRevision: setting.revision,
			title: validation.title,
			titleType: presentationForm.titleType.value,
			description: validation.description,
			descriptionType: presentationForm.descriptionType.value,
		};
		if (presentationForm.banner) payload.banner = presentationForm.banner;

		this.setState({ mutating: true, fieldErrors: {}, actionError: "" });
		try {
			await this.props.Interface.welcomeFreespinsSettingsUpdatePresentation(payload);
			await this.finishMutation("Welcome Freespins presentation was updated");
		} catch (error) {
			if (!this.unmounted) await this.handleMutationFailure(error);
		}
	};

	updateIpPolicy = async () => {
		const { setting, ipMode, ipCheckPeriodDays } = this.state;
		const parsedDays = ipCheckPeriodDays === 0 ? 0 : ipMode === "all" ? null : parsePositiveInteger(ipCheckPeriodDays, MAX_UNSIGNED_INT);
		if (ipCheckPeriodDays !== 0 && ipMode === "custom" && !parsedDays) {
			this.setState({ fieldErrors: { ipCheckPeriodDays: "Enter a positive integer." }, actionError: "Review the highlighted field." });
			return;
		}

		this.setState({ mutating: true, fieldErrors: {}, actionError: "" });
		try {
			await this.props.Interface.welcomeFreespinsSettingsUpdateIpPolicy({
				partnerUid: this.getPartnerUid(),
				expectedVersion: setting.version,
				expectedRevision: setting.revision,
				ipCheckPeriodDays: parsedDays,
			});
			await this.finishMutation("Welcome Freespins IP policy was updated");
		} catch (error) {
			if (!this.unmounted) await this.handleMutationFailure(error);
		}
	};

	disableSetting = async () => {
		const { setting } = this.state;
		this.setState({ mutating: true, actionError: "" });

		try {
			await this.props.Interface.welcomeFreespinsSettingsDisable({
				partnerUid: this.getPartnerUid(),
				expectedVersion: setting.version,
				expectedRevision: setting.revision,
			});
			await this.finishMutation("Welcome Freespins setting was disabled");
		} catch (error) {
			if (!this.unmounted) await this.handleMutationFailure(error);
		}
	};

	renderReadOnlySetting() {
		const { setting, games } = this.state;
		const selectedGame = games.find((game) => game.value === String(setting.gameId));
		const resolvedGameName = setting.gameName || selectedGame?.label;
		const gameName = resolvedGameName ? `${resolvedGameName} (#${setting.gameId})` : `Game #${setting.gameId}`;
		const renderKeyValue = (label, value) => (
			<Box>
				<Box variant="awsui-key-label">{label}</Box>
				<Box>{value}</Box>
			</Box>
		);

		return (
			<Container header={<Header variant="h3">Current setting</Header>}>
				<SpaceBetween size="l">
					<ColumnLayout columns={3} variant="text-grid">
						{renderKeyValue("Status", setting.enabled ? <Badge color="green">Active</Badge> : <Badge color="red">Disabled</Badge>)}
						{renderKeyValue("Version / revision", `${setting.version} / ${setting.revision}`)}
						{renderKeyValue("Provider", "Endorphina")}
						{renderKeyValue("Game", gameName)}
						{renderKeyValue("Spins", setting.spinsCount)}
						{renderKeyValue("Bet amount", `${setting.betAmount} RUB`)}
						{renderKeyValue("Minimum deposit", `${setting.minDepositAmount} RUB`)}
						{renderKeyValue("Cap", setting.cap)}
						{renderKeyValue("Expiration", `${setting.expirationDays} days`)}
						{renderKeyValue("Deposit type", setting.depositType)}
						{renderKeyValue("IP check", setting.ipCheckPeriodDays === 0 ? "Disabled" : setting.ipCheckPeriodDays === null ? "All time" : `${setting.ipCheckPeriodDays} days`)}
					</ColumnLayout>

					<SpaceBetween size="m">
						<Header variant="h3">Presentation</Header>
						<ColumnLayout columns={2} variant="text-grid">
							{renderKeyValue(`Title (${setting.titleType})`, setting.title)}
							{renderKeyValue(`Description (${setting.descriptionType})`, setting.description)}
						</ColumnLayout>
						{setting.bannerUrl ? (
							<Box>
								<Box variant="awsui-key-label">Banner</Box>
								<img src={setting.bannerUrl} alt="Welcome Freespins banner" style={{ display: "block", marginTop: 8, maxWidth: "100%", maxHeight: 220, objectFit: "contain" }} />
							</Box>
						) : null}
					</SpaceBetween>
				</SpaceBetween>
			</Container>
		);
	}

	renderPresentationForm() {
		const { presentationForm, fieldErrors, mutating, setting } = this.state;

		return (
			<Container header={<Header variant="h3">Edit presentation</Header>}>
				<SpaceBetween size="m">
					<FormField label="Title" errorText={fieldErrors.title}>
						<Input value={presentationForm.title} onChange={({ detail }) => this.setPresentationField("title", detail.value)} disabled={mutating} />
					</FormField>
					<FormField label="Title localization" errorText={fieldErrors.titleType}>
						<Select selectedOption={presentationForm.titleType} options={localizationTypeOptions} onChange={({ detail }) => this.setPresentationField("titleType", detail.selectedOption)} disabled={mutating} />
					</FormField>
					<FormField label="Description" errorText={fieldErrors.description}>
						<Textarea value={presentationForm.description} onChange={({ detail }) => this.setPresentationField("description", detail.value)} disabled={mutating} />
					</FormField>
					<FormField label="Description localization" errorText={fieldErrors.descriptionType}>
						<Select selectedOption={presentationForm.descriptionType} options={localizationTypeOptions} onChange={({ detail }) => this.setPresentationField("descriptionType", detail.selectedOption)} disabled={mutating} />
					</FormField>
					<FormField label="Replacement banner (optional PNG)" description="Leave empty to keep the current banner." errorText={fieldErrors.presentationBanner}>
						<input
							ref={(input) => {
								this.presentationBannerInput = input;
							}}
							type="file"
							accept=".png,image/png"
							onChange={(event) => this.readBanner(event, "presentation")}
							disabled={mutating}
						/>
						<img src={presentationForm.bannerPreview || setting.bannerUrl} alt="Welcome Freespins banner preview" style={{ display: "block", marginTop: 8, maxWidth: "100%", maxHeight: 220, objectFit: "contain" }} />
					</FormField>
					<SpaceBetween direction="horizontal" size="s">
						<Button variant="primary" onClick={this.updatePresentation} loading={mutating}>
							Save presentation
						</Button>
						<Button onClick={this.closePresentationEditor} disabled={mutating}>
							Cancel
						</Button>
					</SpaceBetween>
				</SpaceBetween>
			</Container>
		);
	}

	renderIpPolicyForm() {
		const { ipMode, ipCheckPeriodDays, fieldErrors, mutating, setting } = this.state;
		const ipCheckActive = ipCheckPeriodDays !== 0;

		return (
			<Container header={<Header variant="h3">Edit IP policy</Header>}>
				<SpaceBetween size="m">
					<Toggle checked={ipCheckActive} onChange={({ detail }) => this.setIpCheck(detail.checked)} disabled={mutating}>
						Check duplicate registration IP
					</Toggle>
					<FormField label="IP check period">
						<RadioGroup value={ipMode} items={ipPeriodOptions} onChange={({ detail }) => this.setState({ ipMode: detail.value, fieldErrors: {}, actionError: "" })} disabled={mutating || !ipCheckActive} />
					</FormField>
					{ipMode === "custom" ? (
						<FormField label="Custom days" errorText={fieldErrors.ipCheckPeriodDays}>
							<Input value={ipCheckPeriodDays} inputMode="numeric" onChange={({ detail }) => this.setState({ ipCheckPeriodDays: detail.value, fieldErrors: {}, actionError: "" })} disabled={mutating || !ipCheckActive} />
						</FormField>
					) : null}
					<SpaceBetween direction="horizontal" size="s">
						<Button variant="primary" onClick={this.updateIpPolicy} loading={mutating}>
							Save IP policy
						</Button>
						<Button
							onClick={() =>
								this.setState({
									action: null,
									fieldErrors: {},
									actionError: "",
									...ipPolicyFormFromSetting(setting),
								})
							}
							disabled={mutating}>
							Cancel
						</Button>
					</SpaceBetween>
				</SpaceBetween>
			</Container>
		);
	}

	renderCreateForm() {
		const { createForm, fieldErrors, games, gamesLoading, gamesError, mutating, exists, partnerUid } = this.state;
		const selectedGame = games.find((game) => game.value === createForm.gameId) || null;

		return (
			<SpaceBetween size="m">
				{exists ? <Header variant="h2">Create new version</Header> : null}
				{gamesError ? (
					<Alert type="error" action={<Button onClick={this.loadGames}>Retry</Button>}>
						Eligible Endorphina games could not be loaded.
					</Alert>
				) : null}

				<Header variant="h3">Reward and eligibility</Header>
				<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
					<FormField label="Partner UID" description="Exactly one partner UID is required." errorText={fieldErrors.partnerUid}>
						<Input
							value={partnerUid}
							inputMode="numeric"
							onChange={({ detail }) => this.setState({ partnerUid: detail.value, fieldErrors: { ...fieldErrors, partnerUid: "" }, actionError: "" })}
							disabled={mutating || exists}
						/>
					</FormField>
					<FormField label="Provider" description="Fixed reward provider.">
						<Input value="Endorphina" disabled />
					</FormField>
				</Grid>
				<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
					<FormField label="Game" errorText={fieldErrors.gameId}>
						<Select
							filteringType="auto"
							selectedOption={selectedGame}
							options={games}
							statusType={gamesLoading ? "loading" : "finished"}
							loadingText="Loading Endorphina games"
							empty="No active listed Endorphina games"
							placeholder="Select one game"
							onChange={({ detail }) => this.setCreateField("gameId", detail.selectedOption.value)}
							disabled={mutating || gamesError}
						/>
					</FormField>
					<FormField label="Spins count" errorText={fieldErrors.spinsCount}>
						<Input value={createForm.spinsCount} inputMode="numeric" onChange={({ detail }) => this.setCreateField("spinsCount", detail.value)} disabled={mutating} />
					</FormField>
				</Grid>
				<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
					<FormField label="Bet amount (RUB)" errorText={fieldErrors.betAmount}>
						<Input value={createForm.betAmount} inputMode="decimal" onChange={({ detail }) => this.setCreateField("betAmount", detail.value)} disabled={mutating} />
					</FormField>
					<FormField label="Minimum deposit (RUB)" errorText={fieldErrors.minDepositAmount}>
						<Input value={createForm.minDepositAmount} inputMode="decimal" onChange={({ detail }) => this.setCreateField("minDepositAmount", detail.value)} disabled={mutating} />
					</FormField>
				</Grid>
				<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
					<FormField label="Cap" errorText={fieldErrors.cap}>
						<Input value={createForm.cap} inputMode="numeric" onChange={({ detail }) => this.setCreateField("cap", detail.value)} disabled={mutating} />
					</FormField>
					<FormField label="Expiration days" constraintText="Allowed range: 1–365 days." errorText={fieldErrors.expirationDays}>
						<Input value={createForm.expirationDays} inputMode="numeric" onChange={({ detail }) => this.setCreateField("expirationDays", detail.value)} disabled={mutating} />
					</FormField>
				</Grid>
				<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
					<FormField label="Deposit type" errorText={fieldErrors.depositType}>
						<Select selectedOption={createForm.depositType} options={depositTypeOptions} onChange={({ detail }) => this.setCreateField("depositType", detail.selectedOption)} disabled={mutating} />
					</FormField>
					<FormField label="IP anti-fraud">
						<Box padding={{ top: "xxs" }}>
							<Toggle checked={createForm.ipCheckPeriodDays !== 0} onChange={({ detail }) => this.setCreateIpCheck(detail.checked)} disabled={mutating}>
								Check duplicate registration IP
							</Toggle>
						</Box>
					</FormField>
				</Grid>
				<Grid gridDefinition={[{ colspan: 6 }]}>
					<FormField label="IP check period">
						<RadioGroup
							value={createForm.ipMode}
							items={ipPeriodOptions}
							onChange={({ detail }) => this.setCreateField("ipMode", detail.value)}
							disabled={mutating || createForm.ipCheckPeriodDays === 0}
						/>
					</FormField>
				</Grid>
				{createForm.ipMode === "custom" ? (
					<Grid gridDefinition={[{ colspan: 6 }]}>
						<FormField label="Custom IP check days" errorText={fieldErrors.ipCheckPeriodDays}>
							<Input
								value={createForm.ipCheckPeriodDays}
								inputMode="numeric"
								onChange={({ detail }) => this.setCreateField("ipCheckPeriodDays", detail.value)}
								disabled={mutating || createForm.ipCheckPeriodDays === 0}
							/>
						</FormField>
					</Grid>
				) : null}

				<Header variant="h3">Presentation</Header>
				<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
					<FormField label="Title" errorText={fieldErrors.title}>
						<Input value={createForm.title} onChange={({ detail }) => this.setCreateField("title", detail.value)} disabled={mutating} />
					</FormField>
					<FormField label="Title localization" errorText={fieldErrors.titleType}>
						<Select selectedOption={createForm.titleType} options={localizationTypeOptions} onChange={({ detail }) => this.setCreateField("titleType", detail.selectedOption)} disabled={mutating} />
					</FormField>
				</Grid>
				<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
					<FormField label="Description" errorText={fieldErrors.description}>
						<Textarea value={createForm.description} onChange={({ detail }) => this.setCreateField("description", detail.value)} disabled={mutating} />
					</FormField>
					<FormField label="Description localization" errorText={fieldErrors.descriptionType}>
						<Select selectedOption={createForm.descriptionType} options={localizationTypeOptions} onChange={({ detail }) => this.setCreateField("descriptionType", detail.selectedOption)} disabled={mutating} />
					</FormField>
				</Grid>
				<FormField label="Banner (required PNG)" description="Decoded size must not exceed 1 MiB." errorText={fieldErrors.banner}>
					<input
						ref={(input) => {
							this.createBannerInput = input;
						}}
						type="file"
						accept=".png,image/png"
						onChange={(event) => this.readBanner(event, "create")}
						disabled={mutating}
					/>
					{createForm.bannerPreview ? <img src={createForm.bannerPreview} alt="New Welcome Freespins banner preview" style={{ display: "block", marginTop: 8, maxWidth: "100%", maxHeight: 220, objectFit: "contain" }} /> : null}
				</FormField>
			</SpaceBetween>
		);
	}

	renderDisableConfirmation() {
		const { disableConfirmationVisible, mutating } = this.state;

		return (
			<Modal
				visible={disableConfirmationVisible}
				onDismiss={() => !mutating && this.setState({ disableConfirmationVisible: false })}
				header="Disable Welcome Freespins?"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="s">
							<Button onClick={() => this.setState({ disableConfirmationVisible: false })} disabled={mutating}>
								Cancel
							</Button>
							<Button variant="primary" onClick={this.disableSetting} loading={mutating}>
								Disable
							</Button>
						</SpaceBetween>
					</Box>
				}>
				This is irreversible. Pending Welcome Freespins offers will be cancelled, and this version cannot be re-enabled.
			</Modal>
		);
	}

	handleTabChange = (activeTabId) => {
		if (this.state.mutating) return;
		this.setState((state) => ({
			activeTabId,
			statisticsOpened: state.statisticsOpened || activeTabId === "statistics",
		}));
	};

	renderExistingConfiguration() {
		const { setting, action, mutating } = this.state;

		return (
			<SpaceBetween size="m">
				{this.renderReadOnlySetting()}
				{setting.enabled ? (
					<>
						<SpaceBetween direction="horizontal" size="s">
							<Button onClick={this.openPresentationEditor} disabled={mutating}>
								Edit presentation
							</Button>
							<Button onClick={this.openIpEditor} disabled={mutating}>
								Edit IP policy
							</Button>
							<Button onClick={() => this.setState({ disableConfirmationVisible: true, actionError: "" })} disabled={mutating}>
								Disable
							</Button>
						</SpaceBetween>
						{action === "presentation" ? this.renderPresentationForm() : null}
						{action === "ip" ? this.renderIpPolicyForm() : null}
					</>
				) : (
					<>
						<Alert type="warning">This version is disabled and cannot be re-enabled.</Alert>
						{action === "create" ? this.renderCreateForm() : <Button onClick={this.openCreateVersion}>Create new version</Button>}
					</>
				)}
			</SpaceBetween>
		);
	}

	render() {
		const {
			loading,
			currentInitialized,
			initializationError,
			exists,
			setting,
			action,
			actionError,
			mutating,
			partnerUid,
			games,
			gamesLoading,
			gamesError,
			activeTabId,
			statisticsOpened,
		} = this.state;

		return (
			<>
				<Modal
					visible={this.props.visible}
					onDismiss={this.dismissEditor}
					header={partnerUid ? `Welcome Freespins — partner ${partnerUid}` : "Create Welcome Freespins rule"}
					size="large"
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="s">
								<Button onClick={this.dismissEditor} disabled={mutating}>
									Close
								</Button>
								{activeTabId === "configuration" && !loading && currentInitialized && !initializationError && action === "create" ? (
									<Button variant="primary" onClick={this.createSetting} loading={mutating} disabled={gamesLoading || gamesError || games.length === 0}>
										{exists ? "Create new version" : "Create"}
									</Button>
								) : null}
							</SpaceBetween>
						</Box>
					}>
					{loading ? (
						<Spinner size="normal" />
					) : initializationError ? (
						<Alert type="error" header="Unable to initialize setting" action={<Button onClick={this.retryCurrentInitialization}>Retry</Button>}>
							{initializationError}
						</Alert>
					) : (
						<SpaceBetween size="m">
							{actionError ? <Alert type="error">{actionError}</Alert> : null}
							{!exists ? (
								this.renderCreateForm()
							) : (
								<Tabs
									activeTabId={activeTabId}
									onChange={({ detail }) => this.handleTabChange(detail.activeTabId)}
									tabs={[
										{ id: "configuration", label: "Configuration", content: this.renderExistingConfiguration() },
										{
											id: "statistics",
											label: "Statistics",
											content: statisticsOpened ? (
												<WelcomeFreespinsStatisticsTab
													Interface={this.props.Interface}
													partnerUid={setting.partnerUid}
													currentVersion={setting.version}
													cache={this.statisticsCache}
												/>
											) : null,
										},
									]}
								/>
							)}
						</SpaceBetween>
					)}
				</Modal>
				{this.renderDisableConfirmation()}
			</>
		);
	}
}

export default WelcomeFreespinsRuleEditor;
